import { createStore } from '@/common/pinia';
import type { ApiResponse } from '@/common/services/api.service-new';
import type {
  GlcFcs30dClassGroup,
  GlcFcs30dLayerResponse,
  GlcFcs30dTimeseriesResponse,
} from './glc-fcs30d.types';

/**
 * Cache for the GLC_FCS30D product — sibling of `EsaCciStore` minus the scheme
 * dimension (GLC_FCS30D has a single native LCCS scheme). Layer tile-URLs are
 * cached per epoch; per-AOI stats + timeseries are bounded (LRU) since their keys
 * embed the full stringified geometry.
 */
class GlcFcs30dStore {
  /** `/glc-fcs30d/layer` responses keyed by year. `<LandUseTool>` warms this on year change. */
  private layers: Map<number, ApiResponse<GlcFcs30dLayerResponse>> = new Map();

  /** Per-AOI stats keyed by `${year}|${JSON.stringify(coords)}`. */
  private statsCache: Map<string, GlcFcs30dClassGroup[]> = new Map();

  /** Per-AOI yearly timeseries keyed by `${JSON.stringify(coords)}` (year-independent — one call spans all epochs). */
  private timeseriesCache: Map<string, GlcFcs30dTimeseriesResponse> = new Map();

  public getLayer(year: number): ApiResponse<GlcFcs30dLayerResponse> | null {
    return this.layers.get(year) ?? null;
  }

  public setLayer(year: number, layer: ApiResponse<GlcFcs30dLayerResponse>): void {
    this.layers.set(year, layer);
  }

  public getStatsCache(year: number, fingerprint: string): GlcFcs30dClassGroup[] | undefined {
    return this.statsCache.get(GlcFcs30dStore.statsKey(year, fingerprint));
  }

  public setStatsCache(year: number, fingerprint: string, groups: GlcFcs30dClassGroup[]): void {
    GlcFcs30dStore.setBounded(this.statsCache, GlcFcs30dStore.statsKey(year, fingerprint), groups);
  }

  public getTimeseriesCache(fingerprint: string): GlcFcs30dTimeseriesResponse | undefined {
    return this.timeseriesCache.get(fingerprint);
  }

  public setTimeseriesCache(fingerprint: string, ts: GlcFcs30dTimeseriesResponse): void {
    GlcFcs30dStore.setBounded(this.timeseriesCache, fingerprint, ts);
  }

  /**
   * Cap each AOI-keyed cache and evict the oldest once over capacity — the keys
   * embed the full stringified geometry, so without a bound a long session
   * browsing many zones would retain every AOI's coords + response indefinitely.
   * Re-inserting on write keeps the eviction order most-recent-last.
   */
  private static readonly MAX_AOI_CACHE_ENTRIES = 64;

  private static setBounded<V>(cache: Map<string, V>, key: string, value: V): void {
    cache.delete(key);
    cache.set(key, value);
    if (cache.size > GlcFcs30dStore.MAX_AOI_CACHE_ENTRIES) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) {
        cache.delete(oldest);
      }
    }
  }

  private static statsKey(year: number, fingerprint: string): string {
    return `${year}|${fingerprint}`;
  }
}

const useGlcFcs30dStore = createStore(GlcFcs30dStore);

export { useGlcFcs30dStore, type GlcFcs30dStore };
